<template>
  <CookieBanner />
  <!-- Top header -->
  <header class="border-b border-gray-200 header-topo relative">
    <div class="max-w-6xl mx-auto px-4 py-3 flex items-center justify-center">
      <a href="/" class="shrink-0"><img src="/alerg_logo_circle.png" alt="Alerg.Eu" :style="{ height: logoSize + 'px' }" /></a>
      <div class="ml-4">
        <span class="text-2xl font-bold text-blue-800">Alerg.Eu</span>
        <span class="text-lg text-gray-500 ml-2 hidden sm:inline">— {{ t('site_tagline') }}</span>
      </div>
    </div>
  </header>

  <!-- Pinned Telegram button (stays in place on scroll) -->
  <div v-if="!telegramClosed" class="fixed left-4 bottom-4 z-40 w-28 opacity-50 md:opacity-100 hover:opacity-100 transition">
    <a href="https://t.me/alerg_eu" target="_blank" rel="noopener"
       class="block hover:scale-105 transition-transform"
       :title="t('nav.telegram_subscribe')">
      <img src="/telegram_subscribe.png" alt="Telegram" class="w-full h-auto rounded-2xl shadow-md" />
      <span class="absolute left-[7%] right-[7%] top-[56%] h-[24%] flex items-center justify-center text-white font-bold text-[11px] leading-tight text-center drop-shadow-sm px-1">
        {{ t('nav.telegram_subscribe') }}
      </span>
    </a>
    <button @click="closeTelegram" aria-label="Close"
            class="md:hidden absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gray-900 text-white text-xs flex items-center justify-center shadow">
      ✕
    </button>
  </div>

  <!-- Menu bar -->
  <nav class="bg-blue-700 shadow-sm relative">
    <div class="max-w-6xl mx-auto px-4 py-2 flex items-center">
      <!-- Hamburger (mobile) -->
      <button @click="mobileOpen = !mobileOpen" class="md:hidden text-white p-1 mr-2">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path v-if="!mobileOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
          <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>

      <!-- Desktop links -->
      <div class="hidden md:flex items-center gap-6">
        <a href="/" :class="linkClass('home')">{{ t('nav.home') }}</a>
        <a href="/shared_runs" :class="linkClass('shared_runs')">{{ t('nav.shared_runs') }}</a>
        <a href="/forum" :class="linkClass('forum')">{{ t('nav.forum') }}</a>
        <a href="/news" :class="linkClass('news')">{{ t('nav.news') }}</a>
        <a href="/competitions" :class="linkClass('competitions')">{{ t('nav.competitions') }}</a>
        <a href="/coaches" :class="linkClass('coaches')">{{ t('nav.coaches') }}</a>
        <a href="/shops" :class="linkClass('shops')">{{ t('nav.shops') }}</a>
        <a href="/split-calculator" :class="linkClass('split_calculator')">{{ t('nav.split_calculator') }}</a>
      </div>

      <!-- Right side: user + lang -->
      <div class="ml-auto flex items-center gap-4">
        <template v-if="signedIn">
          <!-- Messages icon -->
          <button @click="toggleSidebar" class="relative text-blue-200 hover:text-white transition">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            <span v-if="unreadCount > 0"
                  class="absolute -top-1.5 -right-2 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full">
              {{ unreadCount > 9 ? '9+' : unreadCount }}
            </span>
          </button>
          <div class="relative" @mouseenter="showUserMenu = true" @mouseleave="showUserMenu = false">
            <span class="text-sm text-white font-medium cursor-pointer py-2">{{ userName }}</span>
            <div v-show="showUserMenu" class="absolute right-0 top-full bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px] z-50">
              <a href="/users/edit" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">{{ t('auth.edit_profile') }}</a>
              <a href="/users/sign_out" data-turbo-method="delete" class="block px-4 py-2 text-sm text-red-600 hover:bg-gray-50">{{ t('auth.sign_out') }}</a>
            </div>
          </div>
        </template>
        <template v-else>
          <a href="/users/sign_in" class="text-sm text-blue-200 hover:text-white transition hidden sm:inline">{{ t('auth.sign_in') }}</a>
          <a href="/users/sign_up" class="text-sm text-white bg-blue-600 px-3 py-1 rounded-lg hover:bg-blue-500 transition hidden sm:inline">{{ t('auth.sign_up') }}</a>
        </template>
        <div class="flex gap-1">
          <a href="/set-locale/ro" data-turbo="false" :class="['px-2 py-1 rounded text-xs font-medium', locale === 'ro' ? 'bg-white text-blue-700' : 'bg-blue-600 text-blue-200 hover:bg-blue-500']">RO</a>
          <a href="/set-locale/ru" data-turbo="false" :class="['px-2 py-1 rounded text-xs font-medium', locale === 'ru' ? 'bg-white text-blue-700' : 'bg-blue-600 text-blue-200 hover:bg-blue-500']">RU</a>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <div v-if="mobileOpen" class="md:hidden bg-blue-800 border-t border-blue-600 px-4 py-3 space-y-1">
      <a href="/" :class="mobileLinkClass('home')">{{ t('nav.home') }}</a>
      <a href="/shared_runs" :class="mobileLinkClass('shared_runs')">{{ t('nav.shared_runs') }}</a>
      <a href="/forum" :class="mobileLinkClass('forum')">{{ t('nav.forum') }}</a>
      <a href="/news" :class="mobileLinkClass('news')">{{ t('nav.news') }}</a>
      <a href="/competitions" :class="mobileLinkClass('competitions')">{{ t('nav.competitions') }}</a>
      <a href="/coaches" :class="mobileLinkClass('coaches')">{{ t('nav.coaches') }}</a>
      <a href="/shops" :class="mobileLinkClass('shops')">{{ t('nav.shops') }}</a>
      <a href="/split-calculator" :class="mobileLinkClass('split_calculator')">{{ t('nav.split_calculator') }}</a>
      <template v-if="signedIn">
        <hr class="border-blue-600 my-2" />
        <button @click="toggleSidebar" class="block py-2 text-sm text-blue-200 hover:text-white flex items-center gap-2">
          {{ t('messages.title') }}
          <span v-if="unreadCount > 0" class="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {{ unreadCount > 9 ? '9+' : unreadCount }}
          </span>
        </button>
      </template>
      <template v-if="!signedIn">
        <hr class="border-blue-600 my-2" />
        <a href="/users/sign_in" class="block py-2 text-sm text-blue-200 hover:text-white">{{ t('auth.sign_in') }}</a>
        <a href="/users/sign_up" class="block py-2 text-sm text-white font-medium">{{ t('auth.sign_up') }}</a>
      </template>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import CookieBanner from './CookieBanner.vue'

const props = defineProps({
  signedIn: Boolean,
  userName: String,
  activePage: { type: String, default: '' }
})

const { t, locale } = useI18n()
const logoSize = ref(120)
const mobileOpen = ref(false)
const showUserMenu = ref(false)
const unreadCount = ref(0)
let pollInterval = null

const telegramClosed = ref(false)
try { telegramClosed.value = sessionStorage.getItem('telegram_btn_closed') === '1' } catch {}

function closeTelegram() {
  telegramClosed.value = true
  try { sessionStorage.setItem('telegram_btn_closed', '1') } catch {}
}

async function fetchUnreadCount() {
  try {
    const res = await fetch('/unread_messages_count', { headers: { 'Accept': 'application/json' } })
    if (res.ok) {
      const data = await res.json()
      unreadCount.value = data.count
    }
  } catch {}
}

onMounted(() => {
  if (props.signedIn) {
    fetchUnreadCount()
    pollInterval = setInterval(fetchUnreadCount, 30000)
  }
})

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval)
})

const pageColors = {
  home: { nav: 'bg-blue-700', mobile: 'bg-blue-800 border-blue-600', btn: 'bg-blue-600', btnHover: 'hover:bg-blue-500' },
  shared_runs: { nav: 'bg-green-700', mobile: 'bg-green-800 border-green-600', btn: 'bg-green-600', btnHover: 'hover:bg-green-500' },
  forum: { nav: 'bg-purple-700', mobile: 'bg-purple-800 border-purple-600', btn: 'bg-purple-600', btnHover: 'hover:bg-purple-500' },
  news: { nav: 'bg-orange-700', mobile: 'bg-orange-800 border-orange-600', btn: 'bg-orange-600', btnHover: 'hover:bg-orange-500' },
  competitions: { nav: 'bg-red-700', mobile: 'bg-red-800 border-red-600', btn: 'bg-red-600', btnHover: 'hover:bg-red-500' },
  coaches: { nav: 'bg-teal-700', mobile: 'bg-teal-800 border-teal-600', btn: 'bg-teal-600', btnHover: 'hover:bg-teal-500' },
  shops: { nav: 'bg-amber-700', mobile: 'bg-amber-800 border-amber-600', btn: 'bg-amber-600', btnHover: 'hover:bg-amber-500' },
  split_calculator: { nav: 'bg-indigo-700', mobile: 'bg-indigo-800 border-indigo-600', btn: 'bg-indigo-600', btnHover: 'hover:bg-indigo-500' },
  messages: { nav: 'bg-blue-700', mobile: 'bg-blue-800 border-blue-600', btn: 'bg-blue-600', btnHover: 'hover:bg-blue-500' },
}
const currentColors = computed(() => pageColors[props.activePage] || pageColors.home)
const navBg = computed(() => currentColors.value.nav)
const mobileBg = computed(() => currentColors.value.mobile)

function toggleSidebar() {
  window.dispatchEvent(new CustomEvent('sidebar:toggle'))
  mobileOpen.value = false
}

function linkClass(page) {
  return ['text-sm transition', props.activePage === page ? 'text-white font-medium' : 'text-blue-200 hover:text-white']
}

function mobileLinkClass(page) {
  return ['block py-2 text-sm', props.activePage === page ? 'text-white font-medium' : 'text-blue-200 hover:text-white']
}

</script>
